import { Button, DialogTitle, Text } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import {type FeedMetadataPublic, ReportsService } from "@/client"

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import useCustomToast from "@/hooks/useCustomToast"

const DeleteFeed = ({ id }: { id: FeedMetadataPublic["id"] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const mutation = useMutation({
    mutationFn: () => ReportsService.deleteFeed({ id }),
    onSuccess: () => {
      showSuccessToast("피드가 성공적으로 삭제되었습니다.")
      setIsOpen(false)
      setHasSubmitted(false)
    },
    onError: () => {
      showErrorToast("피드를 삭제하는 동안 오류가 발생했습니다.")
      setHasSubmitted(false)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["feedMetadata"] })
    },
  })

  const handleDelete = () => {
    if (hasSubmitted) return
    setHasSubmitted(true)
    mutation.mutate()
  }

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      role="alertdialog"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" colorPalette="red">
          <FiTrash2 fontSize="16px" />
          피드 삭제
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>피드 삭제</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text mb={4}>
            이 피드는 <strong>영구적으로 삭제</strong>됩니다. 계속하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </Text>
        </DialogBody>

        <DialogFooter gap={2}>
          <DialogActionTrigger asChild>
            <Button variant="subtle" colorPalette="gray" disabled={mutation.isPending}>
              취소
            </Button>
          </DialogActionTrigger>
          <Button
            variant="solid"
            colorPalette="red"
            onClick={handleDelete}
            disabled={mutation.isPending || hasSubmitted}
            loading={mutation.isPending}
          >
            삭제
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

export default DeleteFeed
